import { useState, useEffect, useCallback } from 'react'
import { Typography, Link, Button, Stack, Drawer } from '@mui/material'
import Cookies from 'js-cookie'
import { useApplicationInsights } from 'features/Telemetry/ApplicationInsights'
import { useCachedSettingsState } from 'state/PublicWebApi'
import { useSliceDispatch, useSliceStateSelector } from 'slices/hooks'
import useStoreCookieConsent from './hooks/useStoreCookieConsent'
import { isNewCookieConsentEnabled } from 'utils/cookies'
import { useTranslation } from 'react-i18next'

const name = 'TrrCookiesApproved'
const approvedValue = '1'
const declinedValue = '0'
const TRR_COOKIES_APPROVED_EXPIRES_DAYS = 365

const getCookieVisibleState = () => Cookies.get(name) === undefined

const CookieBanner = () => {
  const trigger = useSliceDispatch()
  const { enable: enableApplicationInsights, isEnabled: isAppInsightsEnabled } =
    useApplicationInsights()

  const {
    headlineText,
    text,
    approveButton,
    declineButton,
    linkUrl,
    linkText,
  } = useCachedSettingsState().cookieInformation

  const { t } = useTranslation()
  const [cookiebarVisibility, setCookiebarVisibility] = useState<boolean>(
    getCookieVisibleState()
  )

  const { isTrrCookiesApproved } = useSliceStateSelector(
    (state) => state.slices.cookies
  )

  useStoreCookieConsent()

  useEffect(() => {
    if (isTrrCookiesApproved) {
      if (!isAppInsightsEnabled) {
        enableApplicationInsights()
      }
    }
  }, [enableApplicationInsights, isAppInsightsEnabled, isTrrCookiesApproved])

  const decline = useCallback(() => {
    setCookiebarVisibility(false)
    Cookies.set(name, declinedValue, {
      expires: TRR_COOKIES_APPROVED_EXPIRES_DAYS,
      secure: true,
    })
    trigger('setCookieApproval', false)
  }, [trigger])

  const approve = useCallback(() => {
    setCookiebarVisibility(false)
    Cookies.set(name, approvedValue, {
      expires: TRR_COOKIES_APPROVED_EXPIRES_DAYS,
      secure: true,
    })
    trigger('setCookieApproval', true)
  }, [trigger])

  if (!cookiebarVisibility) return <></>

  return (
    <Drawer
      data-testid="cookie-banner"
      open
      anchor="bottom"
      hideBackdrop
      variant="persistent"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      PaperProps={{
        elevation: 16,
        sx: (theme) => ({
          backgroundColor: 'primary.light',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          zIndex: theme.zIndex.drawer + 10, //LoggedIn left menu is also a drawer, so we need to make sure this is on top of that
        }),
      }}
    >
      <Stack
        paddingLeft={{ xs: 3, md: 4 }}
        paddingRight={{ xs: 3, md: 4 }}
        paddingTop={{ xs: 3, md: 7 }}
        paddingBottom={{ xs: 3, md: 7 }}
        alignItems="center"
      >
        <Stack
          direction={{ md: 'column', lg: 'row' }}
          justifyContent={{ md: 'center', lg: 'space-between' }}
          alignItems={{ md: 'center', lg: 'flex-end' }}
          gap={2}
          width="100%"
          maxWidth="1248px"
          paddingX={{ lg: 6 }}
        >
          <Stack>
            <Typography 
              variant="h6" 
              component="h2" 
              mb={1}
              id="cookie-banner-title"
            >
              {headlineText}
            </Typography>
            <Typography id="cookie-banner-description">
              {text}{' '}
              <Link href={linkUrl} display={{ xs: 'inline', md: 'none' }}>
                {linkText}
              </Link>
            </Typography>
            <Typography>
              <Link href={linkUrl} display={{ xs: 'none', md: 'inline' }}>
                {linkText}
              </Link>
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {isNewCookieConsentEnabled() && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => trigger('setCookieSettingsOpen', true)}
                data-testid="cookie-banner-settings-button"
              >
                {t('translation:common.settings')}
              </Button>
            )}
            <Button fullWidth onClick={decline}>
              {declineButton}
            </Button>
            <Button fullWidth onClick={approve}>
              {approveButton}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default CookieBanner
